import { useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, DollarSign, Users } from 'lucide-react';

const CaseStudies = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const caseStudies = [
    {
      title: 'Real Estate Lead Generation - Noida',
      platform: 'Meta Ads (Facebook & Instagram)',
      image: 'https://images.pexels.com/photos/7947754/pexels-photo-7947754.jpeg',
      results: [
        { icon: Users, label: 'Leads Generated', value: '800+' },
        { icon: DollarSign, label: 'Cost Per Lead', value: '₹85' },
        { icon: TrendingUp, label: 'ROI Increase', value: '350%' },
      ],
      description:
        'Generated 800+ qualified leads for a premium residential project in Noida with highly targeted Meta Ads campaigns.',
    },
    {
      title: 'Healthcare Provider Growth - Ghaziabad',
      platform: 'Google Ads + Local SEO',
      image: 'https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg',
      results: [
        { icon: Users, label: 'Patient Inquiries', value: '500+' },
        { icon: DollarSign, label: 'Cost Per Acquisition', value: '₹120' },
        { icon: TrendingUp, label: 'Revenue Growth', value: '200%' },
      ],
      description:
        'Helped a multi-specialty hospital in Ghaziabad increase patient appointments by 60% through strategic Google Ads and local SEO.',
    },
    {
      title: 'E-commerce Brand Scaling - Delhi',
      platform: 'Social Media + Influencer Marketing',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d',
      results: [
        { icon: Users, label: 'Reach', value: '2M+' },
        { icon: DollarSign, label: 'Sales Generated', value: '₹50L+' },
        { icon: TrendingUp, label: 'Engagement', value: '450%' },
      ],
      description:
        'Scaled an e-commerce fashion brand from 5K to 50K followers with viral reels and strategic influencer collaborations.',
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % caseStudies.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);
  };

  return (
    <section className="py-24 md:py-32 bg-slate-900" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400 mb-4 block">
            Success Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
            Proven Results That Speak for Themselves
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Real campaigns, real results. See how we've helped businesses across NCR achieve exceptional growth.
          </p>
        </div>

        <div className="relative" data-testid="case-studies-carousel">
          <div className="bg-slate-800 border border-slate-700 rounded-3xl overflow-hidden shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Image Side */}
              <div className="relative h-64 md:h-auto">
                <img
                  src={caseStudies[currentSlide].image}
                  alt={caseStudies[currentSlide].title}
                  className="w-full h-full object-cover"
                  data-testid="case-study-image"
                />
                <div className="absolute top-4 left-4 bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                  {caseStudies[currentSlide].platform}
                </div>
              </div>

              {/* Content Side */}
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <h3
                  className="text-2xl sm:text-3xl font-bold text-white mb-4"
                  data-testid="case-study-title"
                >
                  {caseStudies[currentSlide].title}
                </h3>
                <p className="text-slate-300 leading-relaxed mb-8" data-testid="case-study-description">
                  {caseStudies[currentSlide].description}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  {caseStudies[currentSlide].results.map((result) => {
                    const Icon = result.icon;
                    return (
                      <div key={`${result.label}-${result.value}`} className="text-center" data-testid={`result-${result.label.replace(/\s+/g, '-').toLowerCase()}`}>
                        <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                          <Icon className="w-5 h-5 text-teal-400" />
                        </div>
                        <div className="text-2xl font-black text-teal-400 mb-1">{result.value}</div>
                        <div className="text-xs text-slate-400 font-medium">{result.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevSlide}
              className="w-12 h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="flex gap-2">
              {caseStudies.map((_, idx) => (
                <button
                  key={`case-study-${idx}`}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === currentSlide ? 'w-8 bg-teal-500' : 'w-2 bg-slate-600'
                  }`}
                  data-testid={`carousel-dot-${idx}`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              className="w-12 h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
              data-testid="carousel-next-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;